import { IconButton, Shimmer, ShimmerElementType, TooltipHost } from "@fluentui/react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPodById, getPodsByProviderId } from "../../api/pods";
import { Pod } from "../../api/pods.types";
import { MAX_PATIENTS_BY_CATEGORY } from "../../config/constants";
import { ROUTES } from "../../config/routes";
import PodPersona from "./PodPersona";
import PodPlaceholder from "./PodPlaceholder";
import styles from "./ProviderPod.module.scss";
import { getProviderCategoryByPosition } from "./Util";

interface Props {
  providerId?: string,
}

const getProviderPod = async (providerId: string): Promise<Pod | undefined> => {
  const podResults = await getPodsByProviderId(providerId);

  if (podResults.length === 0) {
    return undefined;
  }

  const podData = await getPodById(podResults[0].id);

  /**
   * In v2, maintain a map cache of pod data to ids as they are fetched.
   * Add a sidebar column to the right side of the component; this will
   * contain buttons for each of the pods in the results (if there is
   * more than one). Clicking the button will fetch the data for that
   * pod, populate the cache, then display that data in the main view.
   * This allows us to display multiple pods with a relatively low data
   * requirement, both up front and on an ongoing basis.
   */

  if (!podData) {
    return undefined;
  }

  return podData;
}

const ProviderPod = ({providerId}: Props) => {
  const [podData, setPodData] = useState<Pod>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!providerId) {
      setIsLoading(false);
      setError('Unable to load pod details.');
    } else {
      setIsLoading(true);
      setError('');

      getProviderPod(providerId)
        .then(pod => {
          setPodData(pod);
          setIsLoading(false);
        })
        .catch(error => {
          setPodData(undefined);
          setError('Unable to load pod details.');
          setIsLoading(false);
        });
    }
  }, [providerId]);

  if (isLoading) {
    return <ProviderPodShimmer />;
  }

  if (error !== '') {
    return (
      <div className={styles.container}>
        <PodPlaceholder full type="error" iconName="Cancel" text={error} />
      </div>
    );
  }

  if (!podData) {
    return (
      <div className={styles.container}>
        <PodPlaceholder full iconName="WebAppBuilderModule" text="This provider is not assigned to a pod." />
      </div>
    );
  }

  const thisProvider = podData.providers.find(provider => provider.providerId === providerId && provider.status === 'ACTIVATED');

  /**
   * Matched to a pod from which the provider has been deactivated.
   * The API should not return results where this is the case, but it was an issue earlier in
   * development, so is being kept for good measure.
   */
  if (!thisProvider) {
    return null;
  }

  const providerCategory = getProviderCategoryByPosition(thisProvider);
  const maxPatients = providerCategory
    ? MAX_PATIENTS_BY_CATEGORY[providerCategory]
    : 0;
  
  const thisPhysician = providerCategory === 'Physician'
    ? thisProvider
    : podData.providers.find(provider => provider.id === podData.leadProviderId);

  return (
    <div className={styles.container}>
      <div className={styles.titleWrapper}>
        <h1 className={styles.title}>{podData.name}</h1>
        <TooltipHost content="View pod">
          <Link to={`${ROUTES.pods}/${podData.id}`} tabIndex={-1} data-is-focusable={false}>
            <IconButton
              iconProps={{ iconName: "NavigateExternalInline" }}
              ariaLabel="View pod"
            />
          </Link>
        </TooltipHost>
      </div>
      <div className={styles.fields}>
        <div className={styles.detailItem}>
          <strong>Position</strong>
          <span>{thisProvider?.position ?? ''}</span>
        </div>
        <div className={styles.detailItem}>
          <strong>Patient limit</strong>
          <span>{maxPatients}</span>
        </div>
        {
          thisProvider!.providerId !== thisPhysician?.providerId && (
            <div className={`${styles.detailItem} ${styles.wide}`}>
              <strong>Physician</strong>
              <span>
                {
                  thisPhysician
                    ? (
                      <div className={styles.personaWrapper}>
                        <PodPersona provider={thisPhysician} className={styles.persona}/>
                        <TooltipHost content="View profile">
                          <Link to={`${ROUTES.providers}/${thisPhysician.providerId}`} tabIndex={-1} data-is-focusable={false}>
                            <IconButton
                              iconProps={{ iconName: "ContactCard" }}
                              ariaLabel="View profile"
                              className={styles.viewButton}
                            />
                          </Link>
                        </TooltipHost>
                      </div>
                    ) : (
                      "No physician is assigned to this pod."
                    )
                  }
                </span>
            </div>
          )
        }
        <div className={styles.detailItem}>
          <strong>Region</strong>
          <span>{podData.region}</span>
        </div>
      </div>
    </div>
  );
}

export default ProviderPod;

const ProviderPodShimmer = () => {
  return (
    <div className={styles.container}>
      <div className={styles.titleWrapper}>
        <h1 className={styles.title}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 27 }, { type: ShimmerElementType.gap, width: 32 }]}/>
        </h1>
        <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 32, width: 32 }]}/>
      </div>
      <div>
        <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 233, width: 450 }]}/>
      </div>
    </div>
  );
}